import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Skeleton } from 'primereact/skeleton';
import { Calendar } from 'primereact/calendar';
import { Sidebar } from 'primereact/sidebar';
import { Dialog } from 'primereact/dialog';
import { Tag } from 'sharedUi/Tag';
import { Input } from 'sharedUi/Input';
import { Dropdown } from 'sharedUi/Dropdown';
import { Tooltip } from 'sharedUi/Tooltip';
import { DataTable } from 'sharedUi/DataTable';
import type { DataTableColumn } from 'sharedUi/DataTable';
import { ExportButton } from 'sharedUi/ExportButton';
import SaasDataTable, { type SaasColumn } from '../../components/common/saas-data-table';
import { Button } from 'sharedUi/Button';
import { formatTimestamp } from 'sharedUi/dateUtils';
import './log-transaction-list.css';
import '../error-analytics/error-analytics.css';

import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { useDebounce } from '../../hooks/useDebounce';
import { sortRows } from '../../utils/client-sort';
import { logTransactionService } from '../../services/log-transaction.service';
import { personnelLookupService } from '../../services/personnel-lookup.service';
import { logMasterService } from '../../services/log-master.service';
import LogTransactionView from './log-transaction-view';
import type { LogTransaction, AuditDashboardFilters, LogTemplate, DashboardData, AuditOverview, LevelBreakdown, TopUser, TopEndpoint, MostRepeatedLog, TemplateHealth, UserOption } from '../../types/log-transaction.types';
import type { LogMaster, LogMasterQueryParams } from '../../types';
import SearchInput from '../../components/common/search-input';
import ProjectFilter from '../../components/common/project-filter';


interface OverviewCardsProps {
  overview: AuditOverview;
  byLevel: LevelBreakdown;
}

/** Animated count, matching the Error Analytics KPI cards. */
function CountUp({ value }: { value: number }) {
  const [v, setV] = useState(0);
  useEffect(() => {
    let raf = 0;
    const t0 = performance.now();
    const step = (now: number) => {
      const p = Math.min((now - t0) / 850, 1);
      setV(value * (1 - Math.pow(1 - p, 3)));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [value]);
  return <>{Math.round(v).toLocaleString()}</>;
}

const KPI_ICONS: Record<string, JSX.Element> = {
  total: <path d="M4 6h16M4 12h16M4 18h10" />,
  info: <><circle cx="12" cy="12" r="9" /><path d="M12 16v-4M12 8h.01" /></>,
  warning: <><path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" /><path d="M12 9v4M12 17h.01" /></>,
  error: <><circle cx="12" cy="12" r="9" /><path d="m15 9-6 6M9 9l6 6" /></>,
};

const OverviewCards = ({ overview, byLevel }: OverviewCardsProps) => {
  // The three level cards are filtered, so Total must be too — otherwise a filter
  // that matches nothing reads "227 / 0 / 0 / 0". The unfiltered total is kept as
  // context underneath.
  const filteredTotal = byLevel.info + byLevel.warning + byLevel.error;
  const isFiltered = filteredTotal !== overview.totalLogs;

  const cards = [
    { key: 'total', label: 'Total Logs', value: filteredTotal, color: '#6366F1', note: isFiltered ? `of ${overview.totalLogs.toLocaleString()} total` : `${overview.todayLogs?.toLocaleString() ?? 0} today` },
    { key: 'info', label: 'Info', value: byLevel.info, color: '#0EA5E9', note: 'routine activity' },
    { key: 'warning', label: 'Warnings', value: byLevel.warning, color: '#F59E0B', note: 'needs attention' },
    { key: 'error', label: 'Errors', value: byLevel.error, color: '#EF4444', note: 'failed operations' },
  ];

  return (
    <div className="ea-kpis">
      {cards.map((c, i) => (
        <div
          key={c.key}
          className="ea-card ea-kpi ea-reveal"
          style={{ animationDelay: `${i * 40}ms`, ['--kc' as string]: c.color } as React.CSSProperties}
        >
          <div className="top">
            <div className="ic" style={{ background: `linear-gradient(135deg, ${c.color}, ${c.color}bb)` }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" width="17">
                {KPI_ICONS[c.key]}
              </svg>
            </div>
            <div className="ea-kpi-meta">
              <div className="lab">{c.label}</div>
              <div className="val num"><CountUp value={c.value} /></div>
            </div>
          </div>
          <div className="ea-sub" style={{ marginTop: 8 }}>{c.note}</div>
        </div>
      ))}
    </div>
  );
};

interface PieSlice { name: string; value: number; color: string; key: string }

const PieCard = ({ title, slices, emptyText, onSliceClick }: {
  title: string;
  slices: PieSlice[];
  emptyText: string;
  onSliceClick?: (key: string) => void;
}) => {
  const data = slices.filter((s) => s.value > 0);
  const total = data.reduce((a, b) => a + b.value, 0);
  const SIZE = 150, r = SIZE / 2, rad = SIZE * 0.46;
  let angle = -Math.PI / 2;

  return (
    <section className="ea-card pad ea-c6 ea-reveal">
      <div className="ea-card-head">
        <div className="ea-title">
          <span className="i" style={{ background: 'linear-gradient(135deg,#06B6D4,#3B82F6)' }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" width="15"><circle cx="12" cy="12" r="9" /><path d="M12 3v9l6 3" /></svg>
          </span>{title}
        </div>
      </div>
      {total === 0 ? (
        <div className="ea-modal-empty">{emptyText}</div>
      ) : (
        <div className="flex items-center gap-4 flex-wrap">
          <svg width={SIZE} height={SIZE} viewBox={`0 0 ${SIZE} ${SIZE}`} className="flex-none">
            {data.map((s) => {
              const frac = s.value / total;
              const a1 = angle + frac * Math.PI * 2;
              const mid = (angle + a1) / 2;
              const x0 = r + rad * Math.cos(angle), y0 = r + rad * Math.sin(angle);
              const x1 = r + rad * Math.cos(a1), y1 = r + rad * Math.sin(a1);
              const d = data.length === 1
                ? `M${r} ${r - rad} A${rad} ${rad} 0 1 1 ${r - 0.01} ${r - rad} Z`
                : `M${r} ${r} L${x0.toFixed(2)} ${y0.toFixed(2)} A${rad} ${rad} 0 ${frac > 0.5 ? 1 : 0} 1 ${x1.toFixed(2)} ${y1.toFixed(2)} Z`;
              const lx = r + rad * 0.6 * Math.cos(mid), ly = r + rad * 0.6 * Math.sin(mid);
              angle = a1;
              return (
                <g key={s.key}>
                  <title>{`${s.name}: ${s.value.toLocaleString()} (${(frac * 100).toFixed(1)}%)`}</title>
                  <path
                    d={d}
                    fill={s.color}
                    stroke="#fff"
                    strokeWidth="1.5"
                    className={onSliceClick ? 'cursor-pointer hover:opacity-85 transition-opacity' : ''}
                    onClick={() => onSliceClick?.(s.key)}
                  />
                  {frac >= 0.07 && (
                    <text x={lx} y={ly + 3} textAnchor="middle" fill="#fff" fontSize="9" fontWeight="700" style={{ pointerEvents: 'none' }}>
                      {Math.round(frac * 100)}%
                    </text>
                  )}
                </g>
              );
            })}
          </svg>
          <div className="ea-legend">
            {data.map((s) => (
              <div className="ea-lg" key={s.key} onClick={() => onSliceClick?.(s.key)}>
                <span className="ea-sw" style={{ background: s.color }} />
                <span className="nm">{s.name}</span>
                <span className="ct num">{s.value.toLocaleString()}</span>
                <span className="pc">{((s.value / total) * 100).toFixed(1)}%</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
};

const LEVEL_PIE_COLORS: Record<string, string> = { info: '#0EA5E9', warning: '#F59E0B', error: '#EF4444' };
const LAYER_PIE_COLORS: Record<string, string> = {
  API: '#F97316', SCREEN: '#3B82F6', FUNCTION: '#22C55E',
  CONFIG: '#6B7280', DB: '#A855F7', SERVER: '#EF4444',
};

// --- TopActivity.tsx ---
interface TopActivityProps {
  topEndpoints: TopEndpoint[];
  mostRepeated: MostRepeatedLog[];
  onEndpointClick?: (endpoint: string) => void;
  onEventCodeClick?: (logCode: string) => void;
}

const RANK_COLORS = {
  blue: { hex: '#3B82F6', grad: 'linear-gradient(135deg,#6366F1,#0EA5E9)' },
  green: { hex: '#22C55E', grad: 'linear-gradient(135deg,#14B8A6,#22C55E)' },
  purple: { hex: '#A855F7', grad: 'linear-gradient(135deg,#6366F1,#A855F7)' },
} as const;

/**
 * Ranked card in the Error Analytics style: 2-digit rank, primary label, a
 * secondary line, a share bar and the count with a unit underneath. The full
 * list scrolls. Long labels truncate so they never run into the count.
 */
const RankedList = ({
  title,
  items,
  color,
  unit = 'logs',
  mono = false,
  onItemClick,
}: {
  title: string;
  items: { label: string; value: number; key: string; sub?: string }[];
  color: 'blue' | 'green' | 'purple';
  /** shown under the count, e.g. "logs" / "occurrences" */
  unit?: string;
  mono?: boolean;
  onItemClick?: (key: string) => void;
}) => {
  const c = RANK_COLORS[color];
  const peak = items[0]?.value || 1;
  // Top-N cut, same control as the Error Analytics ranked cards. The API sends up to 50.
  const [topN, setTopN] = useState(10);
  const shown = items.slice(0, topN);

  return (
    <section className="ea-card pad ea-c6 ea-reveal">
      <div className="ea-card-head">
        <div className="ea-title">
          <span className="i" style={{ background: c.grad }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" width="15"><path d="M4 4h16M4 4v16M8 8h9M8 12h6M8 16h9" /></svg>
          </span>{title}
        </div>
        <div className="ea-seg">
          {[10, 25, 50].map((n) => (
            <button key={n} className={topN === n ? 'on' : ''} onClick={() => setTopN(n)}>Top {n}</button>
          ))}
        </div>
      </div>
      <div className="ea-scrolllist">
        {shown.length > 0 ? shown.map((item, idx) => (
          <div className="ea-rank" key={item.key + idx} title={item.label} onClick={() => onItemClick?.(item.key)}>
            <div className="rk">{String(idx + 1).padStart(2, '0')}</div>
            <div className="body">
              <div className="code" style={mono ? undefined : { fontFamily: 'inherit' }}>{item.label}</div>
              {item.sub && <div className="desc">{item.sub}</div>}
              <div className="prog"><i style={{ width: `${(item.value / peak) * 100}%`, background: c.hex, animationDelay: `${idx * 40}ms` }} /></div>
            </div>
            <div className="rt">
              <div className="oc num">{item.value.toLocaleString()}<small>{unit}</small></div>
            </div>
          </div>
        )) : (
          <div className="ea-modal-empty">No data</div>
        )}
      </div>
    </section>
  );
};

/** Top users as a horizontal strip of cards, sitting below the ranked lists. */
const TopUsersStrip = ({ topUsers, onUserClick }: { topUsers: TopUser[]; onUserClick?: (actorId: string) => void }) => {
  const initials = (n: string) =>
    (n.trim().split(/\s+/).map((w) => w[0]).slice(0, 2).join('') || '?').toUpperCase();

  return (
    <section className="ea-card pad ea-reveal">
      <div className="ea-card-head">
        <div className="ea-title">
          <span className="i" style={{ background: 'linear-gradient(135deg,#14B8A6,#6366F1)' }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" width="15"><path d="M17 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M22 21v-2a4 4 0 0 0-3-3.9M16 3.1a4 4 0 0 1 0 7.8" /></svg>
          </span>Top Users
        </div>
        <span className="ea-sub">
          {topUsers.length} {topUsers.length === 1 ? 'actor' : 'actors'} · by log volume
        </span>
      </div>
      {topUsers.length > 0 ? (
        <div className="ea-users">
          {topUsers.map((u, idx) => (
            <div className="ea-user" key={u.actorId} onClick={() => onUserClick?.(u.actorId)}
              title={`${u.name} — ${u.count.toLocaleString()} logs`}>
              <span className="rk">#{idx + 1}</span>
              <span className="ea-avatar" style={{ background: 'linear-gradient(135deg,#6366F1,#8B5CF6)' }}>{initials(u.name)}</span>
              <span className="nm">{u.name}</span>
              <span className="cnt num">{u.count.toLocaleString()}</span>
            </div>
          ))}
        </div>
      ) : (
        <div className="ea-modal-empty">No logs attributed to a user</div>
      )}
    </section>
  );
};

const TopActivity = ({ topEndpoints, mostRepeated, onEndpointClick, onEventCodeClick }: TopActivityProps) => {
  return (
    <>
      <div className="ea-grid">
        <RankedList
          title="Most Accessed Endpoints"
          items={topEndpoints.map((e) => ({ label: e.endpoint, value: e.count, key: e.endpoint }))}
          color="green"
          unit="requests"
          mono
          onItemClick={onEndpointClick}
        />
        <RankedList
          title="Most Repeated Logs"
          items={mostRepeated.map((l) => ({ label: l.name, value: l.count, key: l.logCode }))}
          color="purple"
          unit="occurrences"
          mono
          onItemClick={onEventCodeClick}
        />
      </div>

    </>
  );
};
// --- SystemHealth.tsx ---
interface SystemHealthProps {
  templateHealth: TemplateHealth;
  onEventCodeClick?: (logCode: string) => void;
}

type HealthBucket = 'activeWithLogs' | 'activeNoLogs' | 'inactive' | 'deleted' | 'total';

const HEALTH_LABELS: Record<HealthBucket, string> = {
  activeWithLogs: 'Active templates that have produced logs',
  activeNoLogs: 'Active templates that have never fired',
  inactive: 'Templates switched off',
  deleted: 'Templates marked deleted',
  total: 'All log templates',
};

/**
 * Lists the templates behind one Log Master Health figure, searchable by event
 * code, description or endpoint. Rows with logs drill into the Activity Log.
 */
const TemplateHealthDialog = ({ bucket, count, onHide, onEventCodeClick }: {
  bucket: HealthBucket | null;
  count: number;
  onHide: () => void;
  onEventCodeClick?: (logCode: string) => void;
}) => {
  const [search, setSearch] = useState('');
  useEffect(() => { setSearch(''); }, [bucket]);

  const { data, isLoading } = useQuery({
    queryKey: ['template-health', bucket],
    queryFn: () => logTransactionService.getTemplateHealthList(bucket as HealthBucket),
    enabled: !!bucket,
    staleTime: 60 * 1000,
  });

  const items = data?.data?.items || [];
  const needle = search.trim().toLowerCase();
  const shown = needle
    ? items.filter((t) =>
      t.logCode.toLowerCase().includes(needle)
      || (t.description || '').toLowerCase().includes(needle)
      || t.endpoints.some((e) => e.toLowerCase().includes(needle)))
    : items;

  return (
    <Dialog
      visible={!!bucket}
      onHide={onHide}
      header={
        <div className="flex items-center gap-2">
          <i className="pi pi-database text-blue-600" style={{ fontSize: '1rem' }} />
          <div>
            <span className="text-base font-semibold text-ink">
              {bucket ? HEALTH_LABELS[bucket] : ''}
            </span>
            <p className="text-xs text-gray-400 font-normal m-0">{count} template{count === 1 ? '' : 's'}</p>
          </div>
        </div>
      }
      style={{ width: '720px', maxWidth: '95vw' }}
      modal
      dismissableMask
      draggable={false}
      resizable={false}
    >
      <SearchInput
        value={search}
        onChange={setSearch}
        placeholder="Search event code, description or endpoint..."
        width="100%"
        className="mb-3"
        testId="AuditDashboard.TemplateHealth.Search"
      />
      {isLoading ? (
        <div className="text-center text-gray-400 text-sm py-8">Loading…</div>
      ) : shown.length === 0 ? (
        <div className="text-center py-8">
          <i className="pi pi-info-circle text-gray-300 mb-2" style={{ fontSize: '1.5rem' }} />
          <p className="text-sm text-gray-400">{needle ? 'Nothing matches that search.' : 'No templates in this group.'}</p>
        </div>
      ) : (
        <div className="flex flex-col gap-1.5 overflow-y-auto overflow-x-hidden" style={{ maxHeight: '55vh' }}>
          {needle && <p className="text-xs text-gray-400 m-0">{shown.length} of {items.length} shown</p>}
          {shown.map((t) => {
            const clickable = t.logCount > 0 && !!onEventCodeClick;
            return (
              <div
                key={t.templateId}
                onClick={() => clickable && onEventCodeClick?.(t.logCode)}
                className={`px-3 py-2 rounded-lg border border-line bg-gray-50 dark:bg-gray-800 transition-all ${clickable ? 'cursor-pointer hover:bg-blue-50 dark:hover:bg-blue-900/20 hover:border-blue-300 dark:hover:border-blue-700' : ''}`}
                title={clickable ? `Show the ${t.logCount} logs for ${t.logCode}` : 'This template has never produced a log'}
              >
                <div className="flex items-center gap-2">
                  <span className="text-xs font-mono font-semibold text-ink truncate flex-1">{t.logCode}</span>
                  {t.layer && <span className="text-[10px] uppercase text-gray-400 font-semibold">{t.layer}</span>}
                  <span className={`text-xs font-bold px-2 py-0.5 rounded-md ${t.logCount > 0 ? 'text-white bg-blue-600' : 'text-gray-500 bg-gray-200 dark:bg-gray-700'}`}>
                    {t.logCount.toLocaleString()} logs
                  </span>
                </div>
                {t.description && (
                  <p className="text-xs text-ink-muted m-0 mt-1 truncate">{t.description}</p>
                )}
                {t.endpoints.length > 0 && (
                  <p className="text-[11px] text-gray-400 font-mono m-0 mt-1 truncate" title={t.endpoints.join('\n')}>
                    {t.endpoints[0]}{t.endpoints.length > 1 ? ` +${t.endpoints.length - 1} more` : ''}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      )}
    </Dialog>
  );
};

const SystemHealth = ({ templateHealth, onEventCodeClick }: SystemHealthProps) => {
  const [bucket, setBucket] = useState<HealthBucket | null>(null);

  return (
    <section className="ea-card pad ea-reveal">
      <div>
        <div className="ea-card-head">
          <div className="ea-title">
            <span className="i" style={{ background: 'linear-gradient(135deg,#F59E0B,#EF4444)' }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" width="15"><path d="M3 12h4l2 6 4-14 2 8h6" /></svg>
            </span>Log Master Health
          </div>
          <span className="ea-sub">Click any figure to see the templates behind it</span>
        </div>
        <div className="ea-grid" style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
          {([
            { key: 'activeWithLogs', label: 'Active (with logs)', icon: 'pi-check-circle', tone: 'bg-green-50 dark:bg-green-900/10 border-green-200 dark:border-green-800/50 hover:bg-green-100 dark:hover:bg-green-900/20', icontone: 'text-green-500', texttone: 'text-green-700 dark:text-green-400' },
            { key: 'activeNoLogs', label: 'Active (no logs)', icon: 'pi-exclamation-circle', tone: 'bg-yellow-50 dark:bg-yellow-900/10 border-yellow-200 dark:border-yellow-800/50 hover:bg-yellow-100 dark:hover:bg-yellow-900/20', icontone: 'text-yellow-500', texttone: 'text-yellow-700 dark:text-yellow-400' },
            { key: 'inactive', label: 'Inactive', icon: 'pi-minus-circle', tone: 'bg-gray-50 dark:bg-gray-800/50 border-line hover:bg-gray-100 dark:hover:bg-gray-800', icontone: 'text-gray-400', texttone: 'text-ink-muted' },
            { key: 'deleted', label: 'Deleted', icon: 'pi-trash', tone: 'bg-red-50 dark:bg-red-900/10 border-red-200 dark:border-red-800/50 hover:bg-red-100 dark:hover:bg-red-900/20', icontone: 'text-red-500', texttone: 'text-red-700 dark:text-red-400' },
          ] as const).map((tile) => (
            <button
              key={tile.key}
              onClick={() => setBucket(tile.key as HealthBucket)}
              className={`${tile.tone} p-3 rounded-xl border flex items-start gap-3 transition-colors text-left w-full cursor-pointer`}
              style={{ gridColumn: 'span 1' }}
              title={`${HEALTH_LABELS[tile.key as HealthBucket]} — click to list them`}
            >
              <i className={`pi ${tile.icon} ${tile.icontone} mt-0.5 text-lg`} />
              <div>
                <p className={`text-xs font-semibold ${tile.texttone} uppercase tracking-wider mb-0.5`}>{tile.label}</p>
                <p className="text-xl font-bold text-ink">{templateHealth[tile.key as HealthBucket]}</p>
              </div>
            </button>
          ))}
        </div>
        <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700/50 flex items-center justify-between">
          <p className="text-xs font-medium text-ink-muted">Total Templates</p>
          <button aria-label="List every log template"
            onClick={() => setBucket('total')}
            className="bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 text-xs font-bold px-2 py-1 rounded border-none cursor-pointer hover:bg-blue-100 dark:hover:bg-blue-900/40 transition-colors"
            title="List every log template"
          >
            {templateHealth.total}
          </button>
        </div>
      </div>

      <TemplateHealthDialog
        bucket={bucket}
        count={bucket ? templateHealth[bucket] : 0}
        onHide={() => setBucket(null)}
        onEventCodeClick={(code) => { setBucket(null); onEventCodeClick?.(code); }}
      />
    </section>
  );
};

// --- ActivityFilterBar.tsx ---
interface ActivityFilterBarProps {
  filters: AuditDashboardFilters;
  allTemplates: LogTemplate[];
  onFilterChange: (key: keyof AuditDashboardFilters, value: any) => void;
  onClearFilters: () => void;
  resolvedTopUsers?: any[];
  onRefresh: () => void;
  refreshing?: boolean;
}

const LEVEL_OPTIONS = [
  { label: 'All Levels', value: '' },
  { label: 'Info', value: 'info' },
  { label: 'Warning', value: 'warning' },
  { label: 'Error', value: 'error' },
];

const LAYER_OPTIONS = [
  { label: 'All Layers', value: '' },
  { label: 'Screen', value: 'screen' },
  { label: 'Function', value: 'function' },
  { label: 'API', value: 'api' },
  { label: 'Config', value: 'config' },
];

const ActivityFilterBar = ({
  filters,
  allTemplates,
  onFilterChange,
  onClearFilters,
  resolvedTopUsers = [],
  onRefresh,
  refreshing = false,
}: ActivityFilterBarProps) => {
  // Debounced search — local input state fires API only after 500ms of inactivity
  const [searchInput, setSearchInput] = useState(filters.search || '');
  useEffect(() => {
    setSearchInput(filters.search || '');
  }, [filters.search]);
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput !== (filters.search || '')) {
        onFilterChange('search', searchInput || undefined);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [searchInput]); // eslint-disable-line react-hooks/exhaustive-deps

  const templateOptions = [
    { label: 'All Templates', value: '' },
    ...allTemplates.map((t) => ({ label: `${t.logCode} - ${t.name}`, value: t.logCode })),
  ];

  const keyFieldOptions = useMemo(() => {
    const keySet = new Set<string>();
    allTemplates.forEach((t) => {
      if (t.parameters && Array.isArray(t.parameters)) {
        t.parameters.forEach((p) => {
          if (p.isKeyField && p.name) {
            keySet.add(p.name.trim());
          }
        });
      }
    });
    const sorted = Array.from(keySet).sort();
    return [
      { label: 'All Key Fields', value: '' },
      ...sorted.map((k) => ({ label: k, value: k })),
    ];
  }, [allTemplates]);

  // Load user list from API for the User dropdown
  const [fetchedUsers, setFetchedUsers] = useState<{ label: string; value: string }[]>([]);
  const [userLoading, setUserLoading] = useState(false);

  const fetchUsers = useCallback((searchStr?: string) => {
    setUserLoading(true);
    logTransactionService.getAllUsers(searchStr)
      .then((res) => {
        const opts = (res.data || []).map((u: UserOption) => ({
          label: u.name || 'Unknown user',
          value: u.actorId,
        }));
        setFetchedUsers(opts);
      })
      .catch(() => setFetchedUsers([]))
      .finally(() => setUserLoading(false));
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const userSearchTimeout = useRef<NodeJS.Timeout | null>(null);
  const handleUserFilter = (e: { filter: string }) => {
    if (userSearchTimeout.current) clearTimeout(userSearchTimeout.current);
    userSearchTimeout.current = setTimeout(() => {
      fetchUsers(e.filter);
    }, 500);
  };

  const userOptions = useMemo(() => {
    const opts = [...fetchedUsers];
    if (filters.actorId && !opts.some(o => o.value === filters.actorId)) {
      const topUser = resolvedTopUsers.find((u: any) => u.actorId === filters.actorId);
      const nameLabel = topUser?.name || 'Unknown user';
      opts.unshift({ label: nameLabel, value: filters.actorId });
    }
    return [{ label: 'All Users', value: '' }, ...opts];
  }, [fetchedUsers, filters.actorId, resolvedTopUsers]);

  /* What is currently narrowing the log stream, each chip removable on its own.
     The date range is deliberately absent — it lives in the page header. */
  const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
  const activeFilters: { key: string; label: string; clear: () => void }[] = [
    ...(filters.level ? [{ key: 'level', label: `Level: ${cap(filters.level)}`, clear: () => onFilterChange('level', '') }] : []),
    ...(filters.layer ? [{ key: 'layer', label: `Layer: ${cap(filters.layer)}`, clear: () => onFilterChange('layer', '') }] : []),
    ...(filters.logCode ? [{ key: 'code', label: `Event: ${filters.logCode}`, clear: () => onFilterChange('logCode', '') }] : []),
    ...(filters.endpoint ? [{ key: 'ep', label: `Endpoint: ${filters.endpoint}`, clear: () => onFilterChange('endpoint', '') }] : []),
    // Never the actorId itself — always the resolved name
    ...(filters.actorId ? [{ key: 'user', label: `User: ${userOptions.find((o) => o.value === filters.actorId)?.label || 'Unknown user'}`, clear: () => onFilterChange('actorId', '') }] : []),
    ...(filters.paramKey ? [{ key: 'param', label: `${filters.paramKey}${filters.paramValue ? `: ${filters.paramValue}` : ''}`, clear: () => { onFilterChange('paramKey', ''); onFilterChange('paramValue', ''); } }] : []),
    ...(filters.search ? [{ key: 'q', label: `Search: ${filters.search}`, clear: () => { setSearchInput(''); onFilterChange('search', ''); } }] : []),
  ];

  const fld = { display: 'flex', flexDirection: 'column' as const, gap: '0.25rem' };

  return (
    <>
      <Tooltip target="[data-pr-tooltip]" />
      {/* Flat toolbar in the Error Analytics style — no field labels, no collapse.
          Each control carries its own "All …" placeholder instead. */}
      <div className="ea-toolbar">
        <div style={{ ...fld, flex: 2, minWidth: 200 }}>
          <SearchInput
            value={searchInput}
            onChange={setSearchInput}
            placeholder="Search message, endpoint or code…"
            width="100%"
            testId="AuditDashboard.Filter.Search"
          />
        </div>
        <div style={{ ...fld, flex: 1, minWidth: 170 }}>
          <Dropdown
            value={filters.logCode ?? ''}
            options={templateOptions}
            optionLabel="label"
            optionValue="value"
            onChange={(e: { value: string }) => onFilterChange('logCode', e.value)}
            placeholder="All Templates"
            className="w-full"
            data-testid="AuditDashboard.Filter.EventCode"
            resetFilterOnHide
            filter
            filterPlaceholder="Search event code…"
            showClear={!!filters.logCode}
            virtualScrollerOptions={{ itemSize: 38 }}
          />
        </div>
        {/* Level — the Level pie drills in here, so it needs a visible control */}
        <div style={{ ...fld, flex: 1, minWidth: 130 }}>
          <Dropdown
            value={filters.level ?? ''}
            options={LEVEL_OPTIONS}
            onChange={(e: { value: string }) => onFilterChange('level', e.value)}
            placeholder="All Levels"
            className="w-full"
            data-testid="AuditDashboard.Filter.Level"
            resetFilterOnHide
          />
        </div>
        <div style={{ ...fld, flex: 1, minWidth: 130 }}>
          <Dropdown
            value={filters.layer ?? ''}
            options={LAYER_OPTIONS}
            onChange={(e: { value: string }) => onFilterChange('layer', e.value)}
            placeholder="All Layers"
            className="w-full"
            data-testid="AuditDashboard.Filter.Layer"
            resetFilterOnHide
          />
        </div>
        <div style={{ ...fld, flex: 1, minWidth: 160 }}>
          <Dropdown
            value={filters.actorId || ''}
            options={userOptions}
            optionLabel="label"
            optionValue="value"
            onChange={(e: { value: string }) => onFilterChange('actorId', e.value)}
            placeholder="All Users"
            className="w-full"
            data-testid="AuditDashboard.Filter.User"
            resetFilterOnHide
            filter
            filterPlaceholder="Search user…"
            onFilter={handleUserFilter}
            loading={userLoading}
            showClear={!!filters.actorId}
            virtualScrollerOptions={{ itemSize: 38 }}
          />
        </div>
        <div style={{ ...fld, flex: 1, minWidth: 150 }}>
          <Dropdown
            value={filters.paramKey ?? ''}
            options={keyFieldOptions}
            optionLabel="label"
            optionValue="value"
            onChange={(e: { value: string }) => {
              onFilterChange('paramKey', e.value);
              if (!e.value) onFilterChange('paramValue', '');
            }}
            placeholder="All Key Fields"
            className="w-full"
            data-testid="AuditDashboard.Filter.ParamKey"
            resetFilterOnHide
            filter
            showClear={!!filters.paramKey}
            virtualScrollerOptions={{ itemSize: 38 }}
          />
        </div>
        {/* Only meaningful once a key field is picked */}
        {filters.paramKey && (
          <div style={{ ...fld, flex: 1, minWidth: 140 }}>
            <Input
              name="paramValue"
              value={filters.paramValue || ''}
              onChange={(e: any) => onFilterChange('paramValue', e.target.value)}
              placeholder={`Search ${filters.paramKey}…`}
              className="w-full"
              data-testid="AuditDashboard.Filter.ParamValue"
            />
          </div>
        )}
        <button aria-label="Reload the log stream" className="ea-btn" style={{ height: 32 }} onClick={onRefresh} disabled={refreshing}
          data-testid="AuditDashboard.Button.RefreshGrid" title="Reload the log stream">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"
            className={refreshing ? 'ea-spin' : undefined}><path d="M21 12a9 9 0 1 1-2.6-6.4M21 3v5h-5" /></svg>Refresh
        </button>
      </div>

      {/* active filters — shows what a drill-through from Overview applied, each removable */}
      {activeFilters.length > 0 && (
        <div className="ea-activefilters">
          <span className="lbl">Filtered by</span>
          {activeFilters.map((f) => (
            <button aria-label="Remove this filter" key={f.key} className="ea-fchip" onClick={f.clear} title="Remove this filter">
              {f.label}
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" width="11"><path d="M18 6 6 18M6 6l12 12" /></svg>
            </button>
          ))}
          <button className="ea-fclear" onClick={onClearFilters} data-testid="AuditDashboard.Button.ClearFilters">Clear all</button>
        </div>
      )}
    </>
  );
};

// --- ActivityTable.tsx ---
interface ActivityTableProps {
  logs: LogTransaction[];
  totalRecords: number;
  loading: boolean;
  first: number;
  rows: number;
  sortField?: string;
  sortOrder?: 1 | -1;
  onPageChange: (event: any) => void;
  onSort: (event: any) => void;
  onRowClick: (log: LogTransaction) => void;
  /** filter toolbar + active-filter chips, rendered inside this card's head */
  filterSlot?: React.ReactNode;
}


const ActivityTable = ({
  logs,
  totalRecords,
  loading,
  first,
  rows,
  sortField,
  sortOrder,
  onPageChange,
  onSort,
  onRowClick,
  filterSlot,
}: ActivityTableProps) => {
  const exportColumns = useMemo(() => [
    { field: 'EventTimeStamp', header: 'Timestamp' },
    { field: 'layer', header: 'Layer' },
    { field: 'logCode', header: 'Event Code' },
    { field: 'message', header: 'Message' },
    { field: 'actor', header: 'Actor' },
    { field: 'endpoint', header: 'Endpoint' },
  ], []);

  const fetchExportData = useCallback(async () => {
    return logs.map((log) => ({
      EventTimeStamp: formatTimestamp(log.EventTimeStamp || log.createdAt),
      layer: log.layer?.toUpperCase() || '-',
      logCode: log.logCode || '-',
      message: log.message || '-',
      actor: log.actorName || '-',
      endpoint: log.endpoint || '-',
    }));
  }, [logs]);

  const page = Math.floor(first / rows) + 1;
  const pageSize = rows;

  // The event code and actor name are resolved from log_master / personnel_master
  // *after* the query — they are not columns on a log row, so the grid cannot sort
  // by them and those headers stay inert. Per-column filtering is off for the same
  // reason: the log stream is filtered from the toolbar above, server-side.
  const gridColumns: SaasColumn<LogTransaction>[] = useMemo(() => [
    {
      field: 'EventTimeStamp', header: 'Timestamp', sortable: true, hideable: false,
      style: { whiteSpace: 'nowrap', width: 170 },
      body: (row) => (
        <span className="num" style={{ color: '#64748B' }}>
          {formatTimestamp(row.EventTimeStamp || row.createdAt)}
        </span>
      ),
    },
    {
      field: 'layer', header: 'Layer', sortable: true, style: { width: 110 },
      body: (row) => <span className="host">{row.layer ? row.layer.toUpperCase() : '-'}</span>,
    },
    {
      field: 'logCode', header: 'Event Code', style: { minWidth: 170 },
      body: (row) => <span className="code">{row.logCode || '-'}</span>,
    },
    {
      field: 'message', header: 'Message', sortable: true, style: { minWidth: 240, maxWidth: 360 },
      body: (row) => <span className="msg" title={row.message || ''}>{row.message || '-'}</span>,
    },
    {
      field: 'actorName', header: 'Actor', style: { minWidth: 140 },
      body: (row) => <span className="host">{row.actorName || '-'}</span>,
    },
    {
      field: 'endpoint', header: 'Endpoint', sortable: true, style: { minWidth: 200 },
      body: (row) => <span className="msg" title={row.endpoint || ''}>{row.endpoint || '-'}</span>,
    },
    {
      field: 'actions', header: 'Actions', hideable: false, align: 'center', style: { width: 80 },
      body: (row) => (
        <button aria-label="View details"
          onClick={(e) => { e.stopPropagation(); onRowClick(row); }}
          className="ea-iconbtn" style={{ width: 28, height: 28 }} title="View details"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="15"><circle cx="12" cy="5" r="1.4" /><circle cx="12" cy="12" r="1.4" /><circle cx="12" cy="19" r="1.4" /></svg>
        </button>
      ),
    },
  ], [onRowClick]);

  return (
    <section className="ea-card ea-reveal">
      <div className="ea-card-head pad" style={{ marginBottom: 0, paddingBottom: filterSlot ? 10 : 14, borderBottom: filterSlot ? 'none' : '1px solid var(--ea-line)', flexWrap: 'wrap', gap: 10 }}>
        <div className="ea-title">
          <span className="i" style={{ background: 'linear-gradient(135deg,#0EA5E9,#6366F1)' }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" width="15"><rect x="3" y="4" width="18" height="16" rx="2" /><path d="M3 9h18M9 4v16" /></svg>
          </span>Log Stream <span className="ea-sub" style={{ fontWeight: 500 }}>· {totalRecords.toLocaleString()} records</span>
        </div>
        <div className="flex items-center gap-2">
          <ExportButton
            fetchData={fetchExportData}
            columns={exportColumns}
            filename="audit-logs"
            testId="AuditDashboard.Button.Export"
          />
        </div>
      </div>
      {filterSlot}

      <SaasDataTable
        tableId="audit-log-stream"
        /* Dashboard layout (stat cards above): grow to content, page scrolls. */
        fillHeight={false}
        columns={gridColumns}
        data={logs}
        loading={loading}
        dataKey="_id"
        emptyMessage="No logs found."
        first={(page - 1) * pageSize}
        rows={pageSize}
        totalRecords={totalRecords}
        onPage={(e: { first: number; rows: number }) => onPageChange({ first: e.first, rows: e.rows })}
        rowsPerPageOptions={[5, 10, 25, 50, 100]}
        sortField={sortField ?? undefined}
        sortOrder={sortOrder ?? undefined}
        onSort={(e: { sortField: string; sortOrder: 1 | -1 | null }) => onSort(e)}
        filters={{}}
        onFilter={() => { /* the log stream filters from the toolbar above */ }}
        testId="AuditDashboard"
      />
    </section>
  );
};

// --- MasterDetailDrawer.tsx ---
interface MasterDetailDrawerProps {
  open: boolean;
  master: LogMaster | null;
  onClose: () => void;
}

const getLevelSeverity = (level: string): 'info' | 'warning' | 'danger' | 'secondary' => {
  switch (level?.toUpperCase()) {
    case 'INFO': return 'info';
    case 'WARNING': case 'WARN': return 'warning';
    case 'ERROR': return 'danger';
    default: return 'secondary';
  }
};



const MasterDetailDrawer = ({ open, master, onClose }: MasterDetailDrawerProps) => {
  if (!master) return null;

  return (
    <Sidebar
      visible={open}
      onHide={onClose}
      position="right"
      style={{ width: '700px' }}
      header={
        <div className="flex items-center gap-3 px-4 py-3">
          <i className="pi pi-file-edit text-blue-600" style={{ fontSize: '1.25rem' }} />
          <span className="text-lg font-semibold text-ink">
            Master Template Details
          </span>
        </div>
      }
    >
      <div className="flex flex-col h-full p-6" style={{ fontSize: '14px' }}>
        <div className="flex-1 overflow-y-auto">
          {/* Event Code & Basic Info */}
          <div className="bg-slate-50 dark:bg-gray-800 border border-slate-200 dark:border-gray-700 rounded-lg p-5 mb-4">
            <div className="mb-4">
              <p className="text-xs font-medium text-slate-500 dark:text-gray-400 uppercase tracking-wide mb-1">Event Code</p>
              <p className="text-sm font-semibold text-blue-600 dark:text-blue-400 font-mono break-all">
                {master.logCode || '-'}
              </p>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <p className="text-xs font-medium text-slate-500 dark:text-gray-400 uppercase tracking-wide mb-1">Layer</p>
                <p className="text-sm text-ink">{master.layer || '-'}</p>
              </div>
              <div>
                <p className="text-xs font-medium text-slate-500 dark:text-gray-400 uppercase tracking-wide mb-1">Log Level</p>
                <Tag value={master.logLevel || 'INFO'} severity={getLevelSeverity(master.logLevel)} className="text-xs" />
              </div>
              <div>
                <p className="text-xs font-medium text-slate-500 dark:text-gray-400 uppercase tracking-wide mb-1">Log Type</p>
                <p className="text-sm text-ink">{master.logtype || '-'}</p>
              </div>
            </div>
          </div>

          {/* Description */}
          {master.description && (
            <div className="bg-blue-50 dark:bg-gray-800 border border-blue-200 dark:border-gray-700 rounded-lg p-5 mb-4">
              <div className="flex items-center gap-2 mb-3">
                <i className="pi pi-info-circle text-blue-600 dark:text-blue-400" style={{ fontSize: '1rem' }} />
                <span className="text-sm font-semibold text-blue-800 dark:text-blue-300">Description</span>
              </div>
              <p className="text-sm text-gray-800 dark:text-gray-200">{master.description}</p>
            </div>
          )}

          {/* Message Template */}
          {master.messageTemplate && (
            <div className="bg-amber-50 dark:bg-gray-800 border border-amber-200 dark:border-gray-700 rounded-lg p-5 mb-4">
              <div className="flex items-center gap-2 mb-3">
                <i className="pi pi-comment text-amber-600 dark:text-amber-400" style={{ fontSize: '1rem' }} />
                <span className="text-sm font-semibold text-amber-800 dark:text-amber-300">Message Template</span>
              </div>
              <p className="text-sm text-gray-800 dark:text-gray-200 font-mono bg-white dark:bg-gray-700 p-3 rounded border border-amber-100 dark:border-gray-600 whitespace-pre-wrap">
                {master.messageTemplate}
              </p>
            </div>
          )}

          {/* Key Fields & Parameters */}
          <div className="bg-teal-50 dark:bg-gray-800 border border-teal-200 dark:border-gray-700 rounded-lg p-5 mb-4">
            <div className="flex items-center gap-2 mb-3">
              <i className="pi pi-key text-teal-600 dark:text-teal-400" style={{ fontSize: '1rem' }} />
              <span className="text-sm font-semibold text-teal-800 dark:text-teal-300">Key Fields & Parameters</span>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-slate-500 dark:text-gray-400 mb-1">Key Fields</p>
                <p className="text-sm text-ink font-mono break-all">
                  {master.parameters && master.parameters.some((p: any) => typeof p === 'object' && p !== null && p.isKeyField)
                    ? master.parameters.filter((p: any) => typeof p === 'object' && p !== null && p.isKeyField).map((p: any) => p.name).join(', ')
                    : (master as any).keyFields || '-'}
                </p>
              </div>
              <div>
                <p className="text-xs text-slate-500 dark:text-gray-400 mb-1">Retention Period</p>
                <p className="text-sm text-ink">{master.retentionPeriod} days</p>
              </div>
            </div>
            {master.parameters && master.parameters.length > 0 && (
              <div className="mt-3">
                <p className="text-xs text-slate-500 dark:text-gray-400 mb-1">Parameters</p>
                <div className="flex flex-wrap gap-1.5">
                  {master.parameters.map((p: any, i) => {
                    const isObj = typeof p === 'object' && p !== null;
                    const name = isObj ? p.name : String(p);
                    const isKey = isObj && p.isKeyField;
                    const isSensitive = isObj && p.isSensitive;

                    let bgClass = "bg-teal-50 text-teal-700 border-teal-200 dark:bg-gray-700 dark:text-teal-300 dark:border-gray-600";
                    if (isSensitive) bgClass = "bg-red-50 text-red-700 border-red-200 dark:bg-red-900/40 dark:text-red-300 dark:border-red-800";
                    else if (isKey) bgClass = "bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/40 dark:text-blue-300 dark:border-blue-800";

                    return (
                      <span key={i} className={`px-2 py-0.5 rounded text-xs flex items-center gap-1 font-mono border ${bgClass}`}>
                        {isKey && <i className="pi pi-key" style={{ fontSize: '0.65rem' }} />}
                        {isSensitive && <i className="pi pi-shield" style={{ fontSize: '0.65rem' }} />}
                        {name}
                      </span>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Template Parameters */}
          {master.templateParameters && Object.keys(master.templateParameters).length > 0 && (
            <div className="bg-purple-50 dark:bg-gray-800 border border-purple-200 dark:border-gray-700 rounded-lg p-5 mb-4">
              <div className="flex items-center gap-2 mb-3">
                <i className="pi pi-database text-purple-600 dark:text-purple-400" style={{ fontSize: '1rem' }} />
                <span className="text-sm font-semibold text-purple-800 dark:text-purple-300">
                  Template Parameters ({Object.keys(master.templateParameters).length} fields)
                </span>
              </div>
              <div className="bg-white dark:bg-gray-700 p-3 rounded-lg">
                {Object.entries(master.templateParameters).map(([key, value]) => (
                  <div key={key} className="flex justify-between py-1.5 border-b border-purple-100 dark:border-gray-600 last:border-0">
                    <span className="text-xs font-semibold text-purple-700 dark:text-purple-400 font-mono">{key}</span>
                    <span className="text-xs text-ink font-mono break-all text-right max-w-[70%]">
                      {typeof value === 'object' && value !== null ? JSON.stringify(value) : String(value) || '-'}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Flags */}
          <div className="bg-gray-50 dark:bg-gray-800 border border-line rounded-lg p-5 mb-4">
            <div className="flex items-center gap-2 mb-3">
              <i className="pi pi-flag text-ink-muted" style={{ fontSize: '1rem' }} />
              <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Flags</span>
            </div>
            <div className="flex gap-3">
              <Tag value={master.isActive ? 'Active' : 'Inactive'} severity={master.isActive ? 'success' : 'danger'} className="text-xs" />
              {master.isUsageTrackable && <Tag value="Usage Trackable" severity="info" className="text-xs" />}
              {(master as any).isSensitive && <Tag value="Sensitive" severity="warning" className="text-xs" />}
            </div>
          </div>

          {/* Metadata */}
          <div className="bg-slate-100 dark:bg-gray-800 border border-slate-300 dark:border-gray-700 rounded-lg p-5">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <p className="text-xs text-slate-500 dark:text-gray-400 mb-0.5">Created At</p>
                <p className="text-xs text-ink font-mono">{formatTimestamp(master.createdAt)}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 dark:text-gray-400 mb-0.5">Updated At</p>
                <p className="text-xs text-ink font-mono">{formatTimestamp(master.updatedAt)}</p>
              </div>
              <div className="col-span-2">
                <p className="text-xs text-slate-500 dark:text-gray-400 mb-0.5">ID</p>
                <p className="text-xs text-gray-600 dark:text-gray-300 font-mono break-all">{master._id}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-line pt-5 mt-5 flex gap-3">
          <Button label="Copy ID" icon="pi pi-copy" onClick={() => navigator.clipboard.writeText(master._id)} data-testid="MasterDetail.Button.CopyId" />
          <Button label="Close" severity="secondary" outlined onClick={onClose} data-testid="MasterDetail.Button.Close" />
        </div>
      </div>
    </Sidebar>
  );
};

const MasterLogTab = ({ onEventCodeClick }: { onEventCodeClick?: (logCode: string) => void }) => {
  const [selectedMaster, setSelectedMaster] = useState<LogMaster | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [data, setData] = useState<(LogMaster & { id: string })[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [first, setFirst] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);

  const debouncedSearch = useDebounce(searchText, 500);

  // How many logs each template has actually produced. The health endpoint already
  // computes this for all templates, so join it in rather than adding a second
  // aggregation to the log_master list.
  const { data: usageResponse } = useQuery({
    queryKey: ['template-health', 'total'],
    queryFn: () => logTransactionService.getTemplateHealthList('total'),
    staleTime: 60 * 1000,
  });
  const usage = useMemo(() => {
    const map = new Map<string, { logCount: number; lastSeen?: string | null }>();
    (usageResponse?.data?.items || []).forEach((t) => {
      map.set(t.templateId, { logCount: t.logCount, lastSeen: t.lastSeen });
    });
    return map;
  }, [usageResponse]);
  const usedCount = useMemo(
    () => (usageResponse?.data?.items || []).filter((t) => t.logCount > 0).length,
    [usageResponse],
  );

  const fetchData = useCallback(async (page: number = 1, size: number = pageSize) => {
    try {
      setLoading(true);
      const response = await logMasterService.getAllPaginated({
        page,
        page_size: size,
        logCode: debouncedSearch || undefined,
      });
      setData((response.items || []).map((item) => ({ ...item, id: item._id })));
      setTotalRecords(response.total || 0);
    } catch (err) {
      console.error('Failed to fetch log masters:', err);
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, pageSize]);

  const handlePageChange = (event: any) => {
    const newPage = Math.floor(event.first / event.rows) + 1;
    setFirst(event.first);
    setPageSize(event.rows);
    fetchData(newPage, event.rows);
  };

  useEffect(() => {
    setFirst(0);
    fetchData(1, pageSize);
  }, [debouncedSearch]); // eslint-disable-line react-hooks/exhaustive-deps

  const exportColumns = useMemo(() => [
    { field: 'logCode', header: 'Event Code' },
    { field: 'description', header: 'Description' },
    { field: 'category', header: 'Category' },
    { field: 'retentionPeriod', header: 'Retention (days)' },
    { field: 'parametersSummary', header: 'Parameters' },
    { field: 'logCount', header: 'Logs Produced' },
  ], []);

  const fetchExportData = useCallback(async (): Promise<any[]> => {
    const params: Omit<LogMasterQueryParams, 'page' | 'page_size'> = {};
    if (debouncedSearch?.trim()) params.logCode = debouncedSearch.trim();
    const data = await logMasterService.getAllForExport(params, pageSize);
    return data.map((item) => {
      const p = item.parameters || [];
      const keyCount = p.filter((x: any) => typeof x === 'object' && x !== null && x.isKeyField).length;
      const sensitiveCount = p.filter((x: any) => typeof x === 'object' && x !== null && x.isSensitive).length;
      return {
        ...item,
        parametersSummary: `${p.length} fields, ${keyCount} keys, ${sensitiveCount} sensitive`,
        logCount: usage.get(item._id)?.logCount ?? 0,
      };
    });
  }, [debouncedSearch, pageSize, usage]);

  const columns: DataTableColumn<LogMaster & { id: string }>[] = useMemo(() => [
    {
      field: 'logCode',
      header: 'Event Code',
      style: { minWidth: '280px' },
      body: (rowData: LogMaster) => (
        <span className="font-mono text-xs text-ink break-all">
          {rowData.logCode || '-'}
        </span>
      ),
    },
    {
      field: 'description',
      header: 'Description',
      sortable: false,
      style: { minWidth: '250px' },
      body: (rowData: LogMaster) => (
        <span
          className="text-xs text-ink-muted line-clamp-1"
          data-pr-tooltip={rowData.description || undefined}
        >
          {rowData.description || '-'}
        </span>
      ),
    },
    {
      field: 'category',
      header: 'Category',
      style: { width: '110px' },
      body: (rowData: LogMaster) => (
        <span className="text-xs text-ink-muted">{rowData.category || '-'}</span>
      ),
    },
    {
      field: 'retentionPeriod',
      header: 'Retention',
      style: { width: '100px' },
      body: (rowData: LogMaster) => (
        <span className="text-xs text-ink-muted tabular-nums">
          {rowData.retentionPeriod ? `${rowData.retentionPeriod} days` : '-'}
        </span>
      ),
    },
    {
      field: 'parameters',
      header: 'Parameters',
      style: { width: '130px' },
      body: (rowData: LogMaster) => {
        const params = rowData.parameters || [];
        if (!params.length) return <span className="text-xs text-gray-400">-</span>;

        let keyCount = 0;
        let sensitiveCount = 0;

        params.forEach((p: any) => {
          if (typeof p === 'object' && p !== null) {
            if (p.isKeyField) keyCount++;
            if (p.isSensitive) sensitiveCount++;
          }
        });

        return (
          <div className="flex flex-col gap-0.5" style={{ lineHeight: '1.2' }}>
            <span className="text-xs text-gray-700 dark:text-gray-300">
              {params.length} field{params.length !== 1 ? 's' : ''}
            </span>
            {keyCount > 0 && (
              <span className="text-xs text-blue-500">
                {keyCount} key{keyCount !== 1 ? 's' : ''}
              </span>
            )}
            {sensitiveCount > 0 && (
              <span className="text-xs text-red-500">
                {sensitiveCount} sensitive
              </span>
            )}
          </div>
        );
      },
    },
    {
      field: 'usage',
      header: 'Usage',
      style: { width: '140px' },
      body: (rowData: LogMaster) => {
        const u = usage.get(rowData._id);
        if (!u || u.logCount === 0) {
          return <span className="text-xs text-gray-400">Never fired</span>;
        }
        return (
          <button
            onClick={() => onEventCodeClick?.(rowData.logCode)}
            className="text-left border-none bg-transparent p-0 cursor-pointer group"
            title={`Show the ${u.logCount.toLocaleString()} logs for ${rowData.logCode}`}
          >
            <span className="text-xs font-bold text-white bg-blue-600 px-2 py-0.5 rounded-md group-hover:bg-blue-700 transition-colors">
              {u.logCount.toLocaleString()} logs
            </span>
            {u.lastSeen && (
              <span className="block text-[10px] text-gray-400 mt-0.5">last {formatTimestamp(u.lastSeen)}</span>
            )}
          </button>
        );
      },
    },
    {
      field: 'actions',
      header: 'Actions',
      headerStyle: { textAlign: 'center' },
      body: (rowData: LogMaster & { id: string }) => (
        <div className="flex justify-center">
          <button
            onClick={() => { setSelectedMaster(rowData); setDrawerOpen(true); }}
            className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 text-xs font-medium"
          >
            View Details
          </button>
        </div>
      ),
      style: { width: '100px' },
    },
  ], [usage, onEventCodeClick]);

  return (
    <>
      <Tooltip target="[data-pr-tooltip]" />
      <section className="ea-card ea-reveal" style={{ marginBottom: 16, padding: '12px 16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <SearchInput
              value={searchText}
              onChange={setSearchText}
              placeholder="Search by event code..."
              testId="MasterLogTab.Search"
              width={220}
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            {usageResponse && (
              <span className="text-xs text-gray-500">
                <b className="text-gray-800 dark:text-gray-200">{usedCount}</b> of {usageResponse.data.total} templates have produced logs
              </span>
            )}
            <span className="text-xs text-gray-500">{totalRecords} {totalRecords === 1 ? 'record' : 'records'}</span>
            <ExportButton
              fetchData={fetchExportData}
              columns={exportColumns}
              filename="log-masters"
              testId="MasterLogTab.Button.Export"
            />
          </div>
        </div>
      </section>

      <div className="ds-card">
        <DataTable
          data={data}
          columns={columns}
          loading={loading}
          emptyMessage="No log masters found"
          dataKey="id"
          paginator
          lazy
          first={first}
          rows={pageSize}
          totalRecords={totalRecords}
          onPage={handlePageChange}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </div>

      <MasterDetailDrawer
        open={drawerOpen}
        master={selectedMaster}
        onClose={() => { setDrawerOpen(false); setSelectedMaster(null); }}
      />
    </>
  );
};

// --- log-transaction-list.tsx ---
/**
 * Audit Log Dashboard
 * Full-featured dashboard with Overview and Activity Log tabs.
 */



const LogTransactionList = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [filters, setFilters] = useState<AuditDashboardFilters>({
    page: 1,
    page_size: 10,
  });
  const [first, setFirst] = useState(0);
  const [selectedLog, setSelectedLog] = useState<LogTransaction | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [dateRange, setDateRange] = useState<Date[] | null>(null);
  /** whether the header date-range control is expanded to its pickers */
  const [customOpen, setCustomOpen] = useState(false);
  // In-page (client-side) sort of the currently-loaded rows. Server search/pagination unchanged.
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<1 | -1 | null>(null);

  // Map tab index to tab param: 0=overview, 1=activity, 2=master (no API call)
  const tabParam = activeTab === 0 ? 'overview' : activeTab === 1 ? 'activity' : 'overview';

  // Fetch dashboard data (single endpoint with tab param)
  const { data: dashboardResponse, isLoading, isFetching, refetch } = useQuery({
    queryKey: ['audit-dashboard', { ...filters, tab: tabParam }],
    queryFn: () => logTransactionService.getDashboard({ ...filters as any, tab: tabParam }),
    staleTime: 60 * 1000,
    refetchInterval: 2 * 60 * 1000,
    // Keep the rendered dashboard on screen while a new filter/page loads, so
    // changing a filter refreshes the grid instead of blanking the whole page.
    placeholderData: keepPreviousData,
  });
  const dashboard: DashboardData | null = dashboardResponse?.data || null;

  // Preload personnel cache on mount
  useEffect(() => {
    personnelLookupService.preload();
  }, []);

  const { data: templatesResponse } = useQuery({
    queryKey: ['audit-dashboard-templates'],
    queryFn: () => logTransactionService.getAllTemplates(),
    staleTime: 5 * 60 * 1000,
  });
  const allTemplates = templatesResponse?.data || [];
  const [resolvedTopUsers, setResolvedTopUsers] = useState<any[]>([]);

  // Filter handlers
  const handleFilterChange = (key: keyof AuditDashboardFilters, value: any) => {
    setFilters((prev) => ({ ...prev, [key]: value || undefined, page: 1 }));
    setFirst(0);
  };

  const topUsers = dashboard?.topUsers || [];

  // Resolve Top User Names
  useEffect(() => {
    if (topUsers.length > 0) {
      const actorIds = [...new Set(topUsers.map(u => u.actorId))].filter(Boolean);
      personnelLookupService.getNamesByIds(actorIds).then(nameMap => {
        const resolved = topUsers.map(u => ({
          ...u,
          name: nameMap.get(u.actorId) || 'Unknown user'
        }));
        setResolvedTopUsers(resolved);
      });
    } else {
      setResolvedTopUsers([]);
    }
  }, [topUsers]);

  const handleDateRangeChange = (dates: Date[] | null) => {
    setDateRange(dates);
    if (dates && dates.length === 2 && dates[0] && dates[1]) {
      setFilters((prev) => ({
        ...prev,
        fromDate: dates[0]!.toISOString(),
        toDate: dates[1]!.toISOString(),
        timeline: undefined,
        page: 1,
      }));
      setFirst(0);
    } else if (!dates) {
      setFilters((prev) => {
        const { fromDate, toDate, timeline, ...rest } = prev;
        return { ...rest, page: 1 };
      });
      setFirst(0);
    }
  };

  /**
   * Set one end of the custom range. Either end alone still filters — "from X
   * onwards" / "up to Y" — and whole days are covered: the start snaps to 00:00
   * and the end to 23:59:59.
   */
  const onCustomDate = (index: 0 | 1, value: Date | null) => {
    const next: (Date | null)[] = [dateRange?.[0] ?? null, dateRange?.[1] ?? null];
    next[index] = value;
    setDateRange(next as Date[]);
    const [start, end] = next;
    if (!start && !end) {
      handleDateRangeChange(null);
      return;
    }
    setFilters((prev) => ({
      ...prev,
      fromDate: start ? new Date(new Date(start).setHours(0, 0, 0, 0)).toISOString() : undefined,
      toDate: end ? new Date(new Date(end).setHours(23, 59, 59, 999)).toISOString() : undefined,
      timeline: undefined,
      page: 1,
    }));
    setFirst(0);
  };

  const handleDatePreset = (preset: string) => {
    setDateRange(null);
    setFilters((prev) => ({
      ...prev,
      timeline: preset,
      fromDate: undefined,
      toDate: undefined,
      page: 1,
    }));
    setFirst(0);
  };

  const clearFilters = () => {
    // The project survives a filter clear. It selects WHICH data the dashboard is
    // reading, not a predicate over it — silently switching the viewer back to
    // "all projects" would change the numbers without touching a filter chip.
    setFilters((prev) => ({ page: 1, page_size: 10, appId: prev.appId }));
    setDateRange(null);
    setFirst(0);
  };

  const handlePageChange = (event: any) => {
    const newPage = Math.floor(event.first / event.rows) + 1;
    setFirst(event.first);
    setFilters((prev) => ({ ...prev, page: newPage, page_size: event.rows }));
  };

  const handleRowClick = (log: LogTransaction) => {
    setSelectedLog(log);
    setDrawerOpen(true);
  };

  // Loading skeleton
  if (isLoading && !dashboard) {
    return (
      <div className="py-2 px-3">
        <div className="flex items-center justify-between mb-2">
          <Skeleton width="280px" height="32px" />
          <Skeleton width="70px" height="32px" />
        </div>
        <div className="grid grid-cols-4 gap-3 mb-2">
          {[1, 2, 3, 4].map((i) => <Skeleton key={i} height="80px" />)}
        </div>
        <div className="grid grid-cols-2 gap-3 mb-2">
          <Skeleton height="240px" />
          <Skeleton height="240px" />
        </div>
        <Skeleton height="320px" />
      </div>
    );
  }

  // Defaults
  const overview = dashboard?.overview || { totalLogs: 0, totalTemplates: 0, todayLogs: 0, weekLogs: 0 };
  const byLevel = dashboard?.byLevel || { info: 0, warning: 0, error: 0 };
  const byLayer = dashboard?.byLayer || {};

  const topEndpoints = dashboard?.topEndpoints || [];
  const mostRepeated = dashboard?.mostRepeated || [];
  const templateHealth = dashboard?.templateHealth || { total: 0, activeWithLogs: 0, activeNoLogs: 0, inactive: 0, deleted: 0 };
  const logs = dashboard?.logs?.items || [];
  // Sort the loaded rows in the browser — no server refetch on sort.
  const displayLogs = sortRows(logs, sortField, sortOrder);

  const hasActiveFilters = [
    filters.level, filters.layer, filters.actorId, filters.logCode, filters.endpoint,
    filters.search, filters.paramKey, filters.paramValue, filters.fromDate, filters.toDate, filters.timeline
  ].some(v => v !== undefined && v !== '' && v !== null);
  const isCustomRange = (!!filters.fromDate || !!filters.toDate) && !filters.timeline;
  const RANGE_LABELS: Record<string, string> = {
    lastHour: 'Last hour', last24Hours: 'Last 24 hours',
    last7Days: 'Last 7 days', last30Days: 'Last 30 days', today: 'Today', thisMonth: 'This month',
  };
  /** What the collapsed date-range trigger reads, mirroring Error Analytics. */
  const rangeLabel = (() => {
    if (!isCustomRange) return RANGE_LABELS[filters.timeline || ''] || 'All time';
    const asLabel = (iso: string) =>
      new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    if (filters.fromDate && filters.toDate) {
      const sameDay = new Date(filters.fromDate).toDateString() === new Date(filters.toDate).toDateString();
      return sameDay ? asLabel(filters.fromDate) : `${asLabel(filters.fromDate)} → ${asLabel(filters.toDate)}`;
    }
    return filters.fromDate ? `From ${asLabel(filters.fromDate)}` : `Up to ${asLabel(filters.toDate!)}`;
  })();

  return (
    <div className="ea-root" data-testid="SCR-AuditDashboard">
      <div className="ea-stack">
      {/* Header */}
      <div className="ea-head">
        <div>
          <div className="ea-crumb"><b>Audit Logs</b><span className="sep">/</span>{rangeLabel}</div>
          <h1>Audit Log Dashboard</h1>
        </div>
          <div className="flex items-center gap-3 flex-wrap justify-end">
            {/* Beside the date range: both bound everything the page shows —
                tiles, trend, top lists and the log grid. */}
            <ProjectFilter
              value={filters.appId ?? ''}
              onChange={(appId) => { setFilters((prev) => ({ ...prev, appId: appId || undefined, page: 1 })); setFirst(0); }}
              testId="AuditDashboard.ProjectFilter"
            />
            {/* Collapsed to the active window; expands to the start/end fields and presets.
                Everything between the two dates is included; both empty means all time. */}
            <div className="ea-daterange">
              {/* Collapses to a bare arrow once open, so the fields fit on the same line */}
              <button className={`ea-dr-trigger ${customOpen ? 'open' : ''}`} onClick={() => setCustomOpen((o) => !o)}
                aria-expanded={customOpen} aria-label={customOpen ? 'Hide date range' : 'Change date range'}
                title={customOpen ? 'Hide date range' : 'Change date range'} data-testid="AuditDashboard.DateRange">
                {!customOpen && (
                  <>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" width="15"><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M8 3v4M16 3v4M3 11h18" /></svg>
                    <span className="l">Date range</span>
                    <b>{rangeLabel}</b>
                  </>
                )}
                <svg className="chev" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" width="13"><path d="m9 6 6 6-6 6" /></svg>
              </button>

              {customOpen && (
                <div className="ea-dr-pop">
                  <div className="fields">
                    <div className="fld">
                      <span className="lbl">Start date</span>
                      <Calendar
                        value={dateRange?.[0] ?? null}
                        onChange={(e) => onCustomDate(0, (e.value as Date) ?? null)}
                        showIcon showButtonBar dateFormat="dd M yy" placeholder="Any"
                        maxDate={dateRange?.[1] ?? new Date()}
                        appendTo={typeof document !== 'undefined' ? document.body : undefined}
                        data-testid="AuditDashboard.Filter.StartDate" style={{ width: 150 }}
                      />
                    </div>
                    <div className="fld">
                      <span className="lbl">End date</span>
                      <Calendar
                        value={dateRange?.[1] ?? null}
                        onChange={(e) => onCustomDate(1, (e.value as Date) ?? null)}
                        showIcon showButtonBar dateFormat="dd M yy" placeholder="Any"
                        minDate={dateRange?.[0] ?? undefined} maxDate={new Date()}
                        appendTo={typeof document !== 'undefined' ? document.body : undefined}
                        data-testid="AuditDashboard.Filter.EndDate" style={{ width: 150 }}
                      />
                    </div>
                  </div>
                  <div className="presets">
                    <span className="lbl">Quick select</span>
                    <div className="ea-seg">
                      {([
                        ['last7Days', 'Past 7 days'],
                        ['last30Days', 'Past 30 days'],
                        ['all', 'All time'],
                      ] as const).map(([value, label]) => (
                        <button key={value}
                          className={!isCustomRange && (filters.timeline || 'all') === value ? 'on' : ''}
                          onClick={() => {
                            if (value === 'all') {
                              setDateRange(null);
                              setFilters((prev) => ({ ...prev, timeline: undefined, fromDate: undefined, toDate: undefined, page: 1 }));
                              setFirst(0);
                            } else {
                              handleDatePreset(value);
                            }
                          }}
                          data-testid={`AuditDashboard.DateRange.${value}`}>{label}</button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {hasActiveFilters && (
              <button aria-label="Clear Filters"
                onClick={clearFilters}
                className="ea-btn"
                style={{ color: '#EF4444', borderColor: 'rgba(239,68,68,.4)' }}
                title="Clear Filters"
              >
                <i className="pi pi-times" style={{ fontSize: '0.75rem' }} />
                <span className="text-sm font-medium">Clear</span>
              </button>
            )}

            <button aria-label="Reload the dashboard"
              onClick={() => refetch()}
              disabled={isFetching}
              className="ea-btn"
              data-testid="AuditDashboard.Button.Refresh"
              title="Reload the dashboard"
            >
              <i className={`pi pi-refresh ${isFetching ? 'pi-spin' : ''}`} style={{ fontSize: '0.75rem' }} />
              Refresh
            </button>
          </div>
      </div>

      {/* Stat Cards */}
      <OverviewCards overview={overview} byLevel={byLevel} />

      {/* Tabs */}
      <div className="ea-maintabs">
        {[
          { label: 'Overview', icon: 'pi-th-large' },
          { label: 'Activity Log', icon: 'pi-list', badge: (dashboard?.logs?.total || 0).toLocaleString(), dot: hasActiveFilters },
          { label: 'Master Log', icon: 'pi-book' },
        ].map((t, i) => (
          <button
            key={t.label}
            onClick={() => setActiveTab(i)}
            className={activeTab === i ? 'on' : ''}
            data-testid={`AuditDashboard.Tab.${t.label.replace(/\s/g, '')}`}
          >
            <i className={`pi ${t.icon}`} style={{ fontSize: '0.8rem' }} />
            {t.label}
            {t.badge !== undefined && (
              <span className="cnt num">{t.badge}</span>
            )}
            {t.dot && <span className="w-1.5 h-1.5 rounded-full bg-orange-500" title="Filters active" />}
          </button>
        ))}
      </div>

      {activeTab === 0 && (
        <>
          <div className="ea-grid">
            <PieCard
              title="Level Distribution"
              emptyText="No level data"
              slices={(['info', 'warning', 'error'] as const).map((k) => ({
                key: k,
                name: k.charAt(0).toUpperCase() + k.slice(1),
                value: byLevel[k] || 0,
                color: LEVEL_PIE_COLORS[k],
              }))}
              onSliceClick={(level) => {
                setFilters((prev) => ({ ...prev, level, page: 1 }));
                setFirst(0);
                setActiveTab(1);
              }}
            />
            <PieCard
              title="Layer Activity"
              emptyText="No layer data"
              slices={Object.entries(byLayer)
                .sort(([, a], [, b]) => b - a)
                .map(([layer, count]) => ({
                  key: layer,
                  name: layer,
                  value: count,
                  color: LAYER_PIE_COLORS[layer] || '#8B5CF6',
                }))}
              onSliceClick={(layer) => {
                // the layer dropdown holds lowercase values
                setFilters((prev) => ({ ...prev, layer: layer.toLowerCase(), page: 1 }));
                setFirst(0);
                setActiveTab(1);
              }}
            />
          </div>
          <TopActivity
            topEndpoints={topEndpoints}
            mostRepeated={mostRepeated}
            onEndpointClick={(endpoint) => {
              setFilters((prev) => ({ ...prev, endpoint, page: 1 }));
              setFirst(0);
              setActiveTab(1);
            }}
            onEventCodeClick={(logCode) => {
              setFilters((prev) => ({ ...prev, logCode, page: 1 }));
              setFirst(0);
              setActiveTab(1);
            }}
          />
          <TopUsersStrip
            topUsers={resolvedTopUsers}
            onUserClick={(actorId) => {
              setFilters((prev) => ({ ...prev, actorId, page: 1 }));
              setFirst(0);
              setActiveTab(1);
            }}
          />
          <SystemHealth
            templateHealth={templateHealth}
            onEventCodeClick={(logCode) => {
              setFilters((prev) => ({ ...prev, logCode, page: 1 }));
              setFirst(0);
              setActiveTab(1);
            }}
          />
        </>
      )}

      {activeTab === 1 && (
        <>
          <ActivityTable
            filterSlot={
              <ActivityFilterBar
                filters={filters}
                allTemplates={allTemplates}
                resolvedTopUsers={resolvedTopUsers}
                onFilterChange={handleFilterChange}
                onClearFilters={clearFilters}
                onRefresh={() => refetch()}
                refreshing={isFetching}
              />
            }
            logs={displayLogs}
            totalRecords={dashboard?.logs?.total || 0}
            loading={isFetching}
            first={first}
            rows={filters.page_size || 10}
            sortField={sortField ?? undefined}
            sortOrder={sortOrder ?? undefined}
            onPageChange={handlePageChange}
            onSort={(e: any) => {
              // Client-side only: reorder the loaded page, no refetch.
              setSortField(e.sortField);
              setSortOrder(e.sortOrder === 1 ? 1 : -1);
            }}
            onRowClick={handleRowClick}
          />
        </>
      )}

      {activeTab === 2 && (
        <MasterLogTab
          onEventCodeClick={(logCode) => {
            setFilters((prev) => ({ ...prev, logCode, page: 1 }));
            setFirst(0);
            setActiveTab(1);
          }}
        />
      )}

      </div>

      {/* Log Detail Drawer */}
      <LogTransactionView
        open={drawerOpen}
        log={selectedLog}
        templates={allTemplates}
        onClose={() => {
          setDrawerOpen(false);
          setSelectedLog(null);
        }}
      />
    </div>
  );
};

export default LogTransactionList;
